import { Clock, AlertTriangle, Info } from 'lucide-react';

interface Props {
  loanNumber: string;
  lastUpdateStatus?: string;
}

export default function MeridianLinkDeferredMonitor({ loanNumber, lastUpdateStatus }: Props) {
  const isDeferred = lastUpdateStatus?.toUpperCase() === 'DEFERRED';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-teal-400" />
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Deferred Update Monitor</h4>
      </div>

      {isDeferred ? (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-500/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Update Deferred</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                The last loan fragment update was deferred because the borrower is currently
                active in the MeridianLink application. The system will automatically poll for
                confirmation (every 5 minutes, up to 24 hours).
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Loan Number</p>
                  <p className="text-xs text-gray-700 dark:text-gray-300 font-mono">{loanNumber}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Status</p>
                  <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    <Clock className="h-2.5 w-2.5" /> DEFERRED
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-amber-500 mt-2">
                Polling checks loan comments for the correlation ID to confirm the update was applied.
                Once confirmed, the delivery workflow will resume automatically.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">No Deferred Updates</p>
              <p className="text-xs text-gray-500 mt-0.5">
                There are no pending deferred updates for this loan. When a loan fragment update is deferred
                (because the borrower is active), it will appear here with polling status and elapsed time.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-500/20">
        <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1.5">How Deferred Updates Work</p>
        <ol className="text-[11px] text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
          <li>A loan fragment update returns DEFERRED status</li>
          <li>The system schedules polling (every 5 minutes)</li>
          <li>Polling checks loan comments for the correlation ID</li>
          <li>Once found, the update is confirmed as applied</li>
          <li>The report delivery workflow resumes automatically</li>
          <li>Maximum polling duration: 24 hours</li>
        </ol>
      </div>
    </div>
  );
}
